import React from "react";
import { Link } from "react-router-dom";
import { GLOBAL_IMAGES } from "../../assets/asset";
import happyChildLearning from "../../assets/global/happychildlearning.svg";
import kidsLearningTogether from "../../assets/global/kidslearningtogether.svg";

const AboutContent = () => {
  const setFallbackImage = (event, fallbackSrc) => {
    event.currentTarget.onerror = null;
    event.currentTarget.src = fallbackSrc;
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          <div className="w-full lg:w-2/3 space-y-6 lg:space-y-8">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-black mb-4 lg:mb-6">
              About MathDingo
            </h2>

            <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
              MathDingo is crafted with Aussie kids in mind, offering an
              engaging, curriculum-aligned learning experience designed to make
              maths fun and accessible. Powered by AI, our app adapts to each
              child&apos;s unique learning style and pace, delivering
              personalised lessons that support their individual needs. Our
              mission is to ignite a passion for maths, turning every lesson
              into an enjoyable journey.
            </p>

            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 lg:mb-4">
                Our Mission:
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                To revolutionise maths education for Aussie kids, throughout all
                curriculums, from early years to Year 12, by offering
                personalised, fun, and accessible learning experiences powered
                by AI.
              </p>
            </div>

            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 lg:mb-4">
                Our Vision:
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                To empower every Australian kid with the confidence and tools to
                embrace maths as an exciting challenge.
              </p>
            </div>

            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-black mb-3 lg:mb-4">
                Our Goals:
              </h3>
              <p className="text-gray-700 leading-relaxed text-sm sm:text-base">
                <strong>Personalised Learning:</strong> Tailor experiences to
                each student&apos;s needs. <strong>Unlimited Practice:</strong>{" "}
                Provide endless opportunities to master maths.{" "}
                <strong>Easy Insights:</strong> Track progress and celebrate
                achievements. <strong>Affordability:</strong> Make quality maths
                education accessible for all. Join us in inspiring Aussie kids
                to love learning and embrace the wonders of maths!
              </p>
            </div>
          </div>

          <div className="w-full lg:w-1/3 relative">
            <img
              src={happyChildLearning}
              alt="Happy child learning"
              className="rounded-2xl w-full h-auto max-w-sm mx-auto lg:max-w-none"
              onError={(event) => setFallbackImage(event, GLOBAL_IMAGES.child)}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-black mb-4 sm:mb-6">
            Discover Our Programmes
          </h2>
          <p className="text-base sm:text-lg text-black max-w-4xl mx-auto px-2">
            At MathDingo, we offer two flexible plans designed to suit the needs
            of every student- whether they&apos;re just starting out or ready to
            take their skills to the next level.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-8 sm:mb-12">
          <div className="bg-pink-100 rounded-3xl p-6 lg:p-8 space-y-4 lg:space-y-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center">
              <img
                src={GLOBAL_IMAGES.coin}
                alt="Basic Plan"
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-cover"
                onError={(event) =>
                  setFallbackImage(event, GLOBAL_IMAGES.badge)
                }
              />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-black">
              Basic Plan
            </h3>
            <ul className="space-y-2 sm:space-y-3 text-gray-700 text-sm sm:text-base">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-pink-400 rounded-full mt-2 flex-shrink-0" />
                Curriculum-aligned lessons
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-pink-400 rounded-full mt-2 flex-shrink-0" />
                Unlimited practice exercises
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-pink-400 rounded-full mt-2 flex-shrink-0" />
                Progress tracking and insights
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-pink-400 rounded-full mt-2 flex-shrink-0" />
                Access to learning content, unlocking progressively by year
              </li>
            </ul>
          </div>

          <div className="bg-yellow-100 rounded-3xl p-6 lg:p-8 space-y-4 lg:space-y-6">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center">
              <img
                src={GLOBAL_IMAGES.medal}
                alt="Pro Plan"
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-cover"
                onError={(event) =>
                  setFallbackImage(event, GLOBAL_IMAGES.badge)
                }
              />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-black">
              Pro Plan
            </h3>
            <p className="text-gray-700 mb-3 sm:mb-4 text-sm sm:text-base">
              For students seeking to accelerate their learning, the Pro Plan
              offers everything in the Basic Plan, plus:
            </p>
            <ul className="space-y-2 sm:space-y-3 text-gray-700 text-sm sm:text-base">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                AI-powered personalised learning.
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                Advanced lessons and challenges.
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-yellow-500 rounded-full mt-2 flex-shrink-0" />
                Priority support and feedback.
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 rounded-3xl p-6 lg:p-8 space-y-4 lg:space-y-6 sm:col-span-2 lg:col-span-1">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center">
              <img
                src={GLOBAL_IMAGES.child}
                alt="Schools/Tutoring centres"
                className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl object-cover"
                onError={(event) =>
                  setFallbackImage(event, GLOBAL_IMAGES.badge)
                }
              />
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-black">
              For Schools/Tutoring centres
            </h3>
            <ul className="space-y-2 sm:space-y-3 text-gray-700 text-sm sm:text-base">
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                &quot;Request to Teacher&quot; feature for real-time assistance
                from teachers.
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                Full control for admins to unlock all or selective years based
                on the needs
              </li>
              <li className="flex items-start gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0" />
                Detailed student insights platform.
              </li>
            </ul>
          </div>
        </div>

        <div className="text-center">
          <p className="text-gray-700 mb-6 sm:mb-8 max-w-4xl mx-auto text-sm sm:text-base px-2">
            Whether your child is just starting or ready to push their limits,
            we have a plan to match their needs. Want to learn more about which
            plan is right for your child? Reach out to us today and discover how
            MathDingo can help them unlock their full potential!
          </p>
          <Link to="/contact">
            <button className="bg-red-400 hover:bg-red-500 text-white font-semibold px-6 sm:px-8 py-2 sm:py-3 rounded-xl transition-colors text-sm sm:text-base">
              Contact us
            </button>
          </Link>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-black mb-4 sm:mb-6 leading-tight px-2">
              Designed To Improve Early Numeracy And Maths Skills From
              Foundation To Year 9
            </h1>

            <div className="flex sm:flex-row items-center justify-center rounded-full overflow-hidden mb-6 sm:mb-8 shadow-lg border-2 border-red-400 mx-auto max-w-lg">
              <div className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 bg-white text-black font-medium flex-1 text-center text-sm sm:text-base">
                Foundation
              </div>
              <div className="px-4 sm:px-6 py-2 sm:py-3 bg-red-400 text-white font-bold flex-shrink-0 text-sm sm:text-base">
                TO
              </div>
              <div className="px-4 sm:px-6 lg:px-8 py-2 sm:py-3 bg-white text-black font-medium flex-1 text-center text-sm sm:text-base">
                Year 9
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-400 to-red-400 rounded-3xl p-6 sm:p-8 lg:p-12 flex flex-col lg:flex-row items-center gap-6 sm:gap-8">
            <div className="w-full lg:w-1/2">
              <img
                src={kidsLearningTogether}
                alt="Kids learning together"
                className="w-full h-auto"
                onError={(event) =>
                  setFallbackImage(event, GLOBAL_IMAGES.child)
                }
              />
            </div>

            <div className="w-full lg:w-1/2">
              <ul className="list-disc pl-6 space-y-4 sm:space-y-6 marker:text-white">
                <li>
                  <h3 className="text-white text-lg sm:text-xl font-bold mb-1 sm:mb-2">
                    Number Sense and Operations
                  </h3>
                  <p className="text-white text-xs sm:text-sm leading-relaxed">
                    Start building strong maths foundations with a number line.
                    Through hands-on activities, students will explore how
                    numbers work, helping them understand number composition and
                    decomposition in a way that&apos;s both fun and practical.
                  </p>
                </li>

                <li>
                  <h3 className="text-white text-lg sm:text-xl font-bold mb-1 sm:mb-2">
                    Geometry
                  </h3>
                  <p className="text-white text-xs sm:text-sm leading-relaxed">
                    Discover the world of shapes! From 2D to 3D, students will
                    explore the defining features of shapes like edges,
                    vertices, cross-sections, and nets through interactive
                    learning that brings geometry to life.
                  </p>
                </li>

                <li>
                  <h3 className="text-white text-lg sm:text-xl font-bold mb-1 sm:mb-2">
                    Logic and Patterns
                  </h3>
                  <p className="text-white text-xs sm:text-sm leading-relaxed">
                    Sharpen problem-solving skills by engaging in puzzles and
                    challenges that get kids thinking critically. Uncover
                    patterns and sequences in everyday situations, making maths
                    both enjoyable and relevant to the world around them.
                  </p>
                </li>

                <li>
                  <h3 className="text-white text-lg sm:text-xl font-bold mb-1 sm:mb-2">
                    Problem Solving
                  </h3>
                  <p className="text-white text-xs sm:text-sm leading-relaxed">
                    Encourage critical thinking through real-life based and
                    interactive problem-solving activities. Students will
                    practice measurement and apply it to practical scenarios,
                    helping them see the value of maths in their daily lives.
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutContent;
